import React from 'react';
import {
  Select,
  MenuItem,
  Divider,
  ListItemText,
  Checkbox,
  Box,
  ListItemIcon,
  Stack,
} from '@mui/material';
import PropTypes from 'prop-types';
import useWindowDimensions from '../../../helpers/hooks';
import { findTargetObject, buildMenuItemListMeta } from './utils';
import appStrings from '../../app_strings';
import appColors from '../../app_colors';
import { useAuthContext } from '../../../providers/auth-provider';
import { useUserPermissionsQuery } from '../../../redux/apis/permissions.api';
import USER_PERMISSIONS from '../../../permissions/user.permissions';

const MENU_ITEM_STRINGS = appStrings.post.createPost;
const { POST_TO_MULTIPLE_TARGETS } = USER_PERMISSIONS;
const { selectInput } = appColors;
const panelIcon = '/assets/icons/panel_icon_grey.svg';
const networkIcon = '/assets/icons/networks_icon_grey.svg';

const dividerStyles = {
  opacity: '1 !important',
  pointerEvents: 'none !important',
  userSelect: 'none !important',
};

const renderMenuItems = (targetMeta, width, selectedValues) => {
  const listItemMeta = buildMenuItemListMeta(targetMeta);
  const items = listItemMeta.map((item) => {
    if (item?.isDivider) {
      return item?.render ? (
        <Stack key={item.targetKey}>
          <MenuItem key={item.targetKey} disableRipple disabled sx={dividerStyles}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
              <ListItemIcon>
                <img
                  src={item.label === MENU_ITEM_STRINGS.panels ? panelIcon : networkIcon}
                  alt="team icon"
                  style={{ width: '24px', height: '24px', padding: '2px' }}
                />
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </Box>
          </MenuItem>
          <Divider component="li" sx={{ borderBottom: `0.5px solid ${selectInput.greyDivider}` }} />
        </Stack>
      ) : null;
    }

    const isChecked = selectedValues.includes(item.targetKey);
    return (
      <MenuItem
        key={item.targetKey}
        value={item.targetKey}
        disableRipple
        sx={{
          backgroundColor: isChecked ? selectInput.selected : 'inherit',
          '&:hover': {
            backgroundColor: isChecked ? selectInput.selected : selectInput.backgroundColor,
          },
          '&.Mui-selected': {
            backgroundColor: `${selectInput.selected} !important`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `${selectInput.selected} !important`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={isChecked}
            style={{ marginRight: '8px' }}
          />
          <ListItemText
            primaryTypographyProps={{
              fontWeight: 500,
            }}
            primary={item.label}
          />
        </Box>
      </MenuItem>
    );
  });

  return items;
};

const selectStyles = {
  backgroundColor: selectInput.backgroundColor,
  height: '30px',
  width: 'fit-content',
  maxWidth: '300px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const PostTargetDropdown = ({
  handleVisibility = () => { },
  targetListMeta = {},
  selectedTargets = [],
  isDisabled = false,
}) => {
  const { width } = useWindowDimensions();
  const selectedValues = selectedTargets || [];
  const { user } = useAuthContext();
  const { data: userPermissions } = useUserPermissionsQuery({ user }, { skip: !user });
  const hasMultiSelectPrivilege = !!userPermissions?.[POST_TO_MULTIPLE_TARGETS]?.allowed;

  const onChange = (event) => {
    const { target: { value } } = event;

    const updatedSelections = hasMultiSelectPrivilege ? value : value.slice(-1);
    const foundTargets = updatedSelections.map((val) => findTargetObject({
      targetKey: val,
      targets: targetListMeta,
      groupType: val.split('-')[0],
    }));
    return handleVisibility(foundTargets);
  };

  const renderDisplayValue = (selected) => {
    if (selected.length === 0) {
      return MENU_ITEM_STRINGS.selectOption;
    }
    const selectedLabels = selected.map((selectedKey) => {
      // Check all sections in targetListMeta to find the label for each targetKey
      const foundTarget = [
        ...targetListMeta.visibility,
        ...targetListMeta.teams,
        ...targetListMeta.groups,
      ].find((item) => item.targetKey === selectedKey);

      return foundTarget ? foundTarget.label : selectedKey;
    });

    return selectedLabels.join(', ');
  };

  return (
    <Select
      sx={selectStyles}
      slot="end"
      multiple
      value={selectedValues}
      renderValue={renderDisplayValue}
      onChange={onChange}
      displayEmpty
      disabled={isDisabled || !userPermissions}
      inputProps={{ 'aria-label': 'Without label' }}
      MenuProps={{
        disableAutoFocusItem: true,
        autoFocus: false,
      }}
    >
      {renderMenuItems(targetListMeta, width, selectedValues)}
    </Select>
  );
};

PostTargetDropdown.propTypes = {
  targetListMeta: PropTypes.oneOfType([PropTypes.object]),
  handleVisibility: PropTypes.func,
  selectedTargets: PropTypes.arrayOf(PropTypes.string),
  isDisabled: PropTypes.bool,
};


export default PostTargetDropdown;
